import React, { useState, useMemo, useEffect } from "react";
import { Search, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreVertical } from "lucide-react";
import { Button } from "../../@/components/ui/button";
import { Card, CardContent } from "../../@/components/ui/card"
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../@/components/ui/dropdown-menu";
import { Badge } from "../../@/components/ui/badge"
import { Label } from "../../@/components/ui/label"
import { Input } from "../../@/components/ui/input"
import DefaultTable from "./defaultTable";
import Pagination from "./pagination";
import DeleteStudentCohortEnrollment from "@/forms/deleteStudentCohortEnrollment";
import UpdateCohortEnrollmentStatus from "@/forms/updateCohortEnrollmentStatus";
import BatchUpdateCohortEnrollmentStatus from "@/forms/batchUpdateCohortEnrollmentStatus";
import BatchDeleteStudentCohortEnrollment from "@/forms/batchDeleteStudentCohortEnrollment";

const StatusBadge = ({ status }) => {
  const raw = status == null ? "" : String(status);
  const normalized = raw.trim();
  const upper = normalized.toUpperCase();
  const display = upper
    ? upper
        .replace(/[_-]+/g, " ")
        .toLowerCase()
        .replace(/^./, (c) => c.toUpperCase())
    : "Unknown";

  return (
    <Badge variant="" className={upper === "ACTIVE" ? "" : "bg-(--color-primary) rounded-full"}>
      {display}
    </Badge>
  )
}

const CohortBadge = ({ cohort }) => {
  return (
    <Badge variant="outline" className="">
      {cohort}
    </Badge>
  )
}

const CourseBadge = ({ course }) => {
  return (
    <p className="">
      {course}
    </p>
  );
}

const StudentTable = ({
  data,
  cohorts,
  selectedCohortId,
  selectedCourse,
  searchTerm,
  setSelectedCohortId,
  onEnrollmentDeleted,
  onEnrollmentUpdated,
  allowEnrolmentActions = true,
  hideCohortColumn = false,
  showSelectedCount = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [deleteEnrollmentDialogOpen, setDeleteEnrollmentDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [updateEnrollmentStatusDialogOpen, setUpdateEnrollmentStatusDialogOpen] =
    useState(false);
  const [selectedEnrollmentForStatus, setSelectedEnrollmentForStatus] = useState(null);
  const [bulkUpdateStatusDialogOpen, setBulkUpdateStatusDialogOpen] = useState(false);
  const [bulkEnrollmentsForStatus, setBulkEnrollmentsForStatus] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkEnrollmentsForDelete, setBulkEnrollmentsForDelete] = useState([]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage) || 1)
  const safeCurrentPage = Math.max(1, Math.min(currentPage, totalPages))
  const startIndex = (safeCurrentPage - 1) * itemsPerPage
  const paginatedData = data.slice(startIndex, startIndex + itemsPerPage)

  const getRowKey = (student) =>
    student?.row_id ??
    student?.rowKey ??
    student?.enrolment_id ??
    student?.enrollment_id ??
    // Prefer user id when present (don’t overwrite `id`, it’s used elsewhere)
    student?.user_id ??
    student?.userId ??
    // Fallback to id only if it really is the unique row identifier
    student?.id ??
    JSON.stringify(student);

  const paginatedDataWithRowKeys = useMemo(
    () =>
      paginatedData.map((student) => ({
        ...student,
        rowKey: getRowKey(student),
      })),
    [paginatedData]
  );

  const pageRowKeys = useMemo(
    () => paginatedDataWithRowKeys.map((student) => student.rowKey),
    [paginatedDataWithRowKeys]
  );

  const toggleSelectRow = (rowKey) =>
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      copy.has(rowKey) ? copy.delete(rowKey) : copy.add(rowKey);
      return copy;
    });

  const toggleSelectAllOnPage = (checked) =>
    setSelectedIds((prev) => {
      const copy = new Set(prev);

      if (checked) {
        pageRowKeys.forEach((k) => copy.add(k));
        return copy;
      }

      pageRowKeys.forEach((k) => copy.delete(k));
      return copy;
    });

  const deriveEnrollmentIds = (student) => {
    const derivedCohortId =
      student?.cohort_id ??
      student?.cohortId ??
      (Array.isArray(cohorts)
        ? cohorts.find((c) => c?.name === student?.cohort)?.id
        : undefined);

    const derivedUserId = student?.user_id ?? student?.userId ?? student?.id;
    const derivedCourseId = student?.course_id ?? student?.courseId;

    return {
      cohortId: derivedCohortId,
      userId: derivedUserId,
      courseId: derivedCourseId,
    };
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [data, itemsPerPage]);

  useEffect(() => {
    if (safeCurrentPage !== currentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [safeCurrentPage, currentPage]);

  const hasSearch = (searchTerm || "").trim().length > 0;
  const hasActiveFilters =
    (selectedCohortId && selectedCohortId !== "ALL") ||
    (selectedCourse && selectedCourse !== "ALL");

  // const handlePageChange = (page) => {
  //   setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  // }

  // const itemsPerPageOptions = [10, 25, 50, 100];

  const columns = [
    {
      key: "cohort",
      header: "Cohort",
      render: (student) => <CohortBadge cohort={student.cohort} />,
    },
    {
      key: "status",
      header: "Status",
      render: (student) => <StatusBadge status={student.status} />,
    },
    {
      key: "name",
      header: "Name",
      render: (student) => (
        <span className="font-body text-(--neutral-800)">{student.name}</span>
      ),
    },
    {
      key: "course",
      header: "Course",
      render: (student) => (
        <span className="font-body text-(--neutral-800)">{student.course}</span>
      ),
    },
    {
      key: "total",
      header: "Total",
      render: (student) => (
        <span className="font-body text-(--neutral-800)">{student.total}</span>
      ),
    },
  ];

  if (allowEnrolmentActions) {
    columns.push({
      key: "action",
      header: "",
      render: (student) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-(--neutral-900)"
              aria-label="More actions"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                const derivedCohortId =
                  student.cohort_id ??
                  student.cohortId ??
                  (Array.isArray(cohorts)
                    ? cohorts.find((c) => c?.name === student.cohort)?.id
                    : undefined);

                const derivedUserId =
                  student.user_id ?? student.userId ?? student.id;

                const derivedCourseId = student.course_id ?? student.courseId;

                setSelectedEnrollmentForStatus({
                  cohortId: derivedCohortId,
                  userId: derivedUserId,
                  courseId: derivedCourseId,
                  studentName: student.name,
                  cohortName: student.cohort,
                  courseName: student.course,
                  currentStatus: student.status,
                });
                setUpdateEnrollmentStatusDialogOpen(true);
              }}
            >
              Change status
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-(--color-destructive)"
              onClick={(e) => {
                e.stopPropagation();
                const derivedCohortId =
                  student.cohort_id ??
                  student.cohortId ??
                  (Array.isArray(cohorts)
                    ? cohorts.find((c) => c?.name === student.cohort)?.id
                    : undefined);

                const derivedUserId =
                  student.user_id ?? student.userId ?? student.id;

                const derivedCourseId = student.course_id ?? student.courseId;

                console.log("Delete cohort enrolment (derived ids)", {
                  cohortId: derivedCohortId,
                  userId: derivedUserId,
                  courseId: derivedCourseId,
                  row: student,
                });

                setSelectedEnrollment({
                  cohortId: derivedCohortId,
                  userId: derivedUserId,
                  courseId: derivedCourseId,
                  studentName: student.name,
                  cohortName: student.cohort,
                });
                setDeleteEnrollmentDialogOpen(true);
              }}
            >
              Delete enrollment
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    });
  }

  const visibleColumns = hideCohortColumn
    ? columns.filter((c) => c.key !== "cohort")
    : columns;

  return (
    <div className="w-full space-y-6">
      {/* Table */}
          <DefaultTable
          columns={visibleColumns}
          data={paginatedDataWithRowKeys}
          emptyMessage={
            hasSearch
              ? "No items match your search"
              : hasActiveFilters
              ? "No items found. Try switching the filter above"
              : "No students found."
          }
          selectedItems={[...selectedIds]}
          onSelectItem={toggleSelectRow}
          onSelectAll={toggleSelectAllOnPage}
          bulkActions={
            allowEnrolmentActions
              ? ["Change status", "Delete enrollment"]
              : []
          }
          onBulkAction={(label, ctx) => {
            const normalized = String(label || "").trim().toLowerCase();
            const selectedRows = Array.isArray(ctx?.selectedRowsOnPage)
              ? ctx.selectedRowsOnPage
              : [];

            if (normalized === "change status") {
              const enrollments = selectedRows
                .map((student) => {
                  const ids = deriveEnrollmentIds(student);
                  return {
                    user_id: ids.userId,
                    cohort_id: ids.cohortId,
                    course_id: ids.courseId,
                    studentName: student?.name,
                    cohortName: student?.cohort,
                    courseName: student?.course,
                  };
                })
                .filter(
                  (e) =>
                    Boolean(String(e.user_id ?? "").trim()) &&
                    e.cohort_id != null &&
                    e.course_id != null
                );

              setBulkEnrollmentsForStatus(enrollments);
              setBulkUpdateStatusDialogOpen(true);
              return;
            }

            if (normalized === "delete enrollment") {
              const enrollments = selectedRows
                .map((student) => {
                  const ids = deriveEnrollmentIds(student);
                  return {
                    user_id: ids.userId,
                    cohort_id: ids.cohortId,
                    course_id: ids.courseId,
                    studentName: student?.name,
                    cohortName: student?.cohort,
                    courseName: student?.course,
                  };
                })
                .filter(
                  (e) =>
                    Boolean(String(e.user_id ?? "").trim()) &&
                    e.cohort_id != null &&
                    e.course_id != null
                );

              setBulkEnrollmentsForDelete(enrollments);
              setBulkDeleteDialogOpen(true);
            }
          }}
        />
      {/* Pagination */}
      
      <Pagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        rowsPerPage={itemsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onRowsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
        onPageChange={(page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)))}
        totalRows={data.length}
        rowLabel="row items"
        selectedCount={showSelectedCount ? selectedIds.size : 0}
        showSelectedCount={showSelectedCount}
      />

      {allowEnrolmentActions && (
        <BatchDeleteStudentCohortEnrollment
          open={bulkDeleteDialogOpen}
          onOpenChange={(nextOpen) => {
            setBulkDeleteDialogOpen(nextOpen);
            if (!nextOpen) setBulkEnrollmentsForDelete([]);
          }}
          selectedEnrollments={bulkEnrollmentsForDelete}
          refetchEnrolments={onEnrollmentDeleted}
          onDeleted={() => {
            setBulkDeleteDialogOpen(false);
            setBulkEnrollmentsForDelete([]);
            setSelectedIds(new Set());
          }}
        />
      )}

      {allowEnrolmentActions && (
        <BatchUpdateCohortEnrollmentStatus
          open={bulkUpdateStatusDialogOpen}
          onOpenChange={(nextOpen) => {
            setBulkUpdateStatusDialogOpen(nextOpen);
            if (!nextOpen) setBulkEnrollmentsForStatus([]);
          }}
          selectedEnrollments={bulkEnrollmentsForStatus}
          refetchEnrolments={onEnrollmentUpdated}
          onUpdated={() => {
            setBulkUpdateStatusDialogOpen(false);
            setBulkEnrollmentsForStatus([]);
            setSelectedIds(new Set());
          }}
        />
      )}

      {allowEnrolmentActions && (
        <UpdateCohortEnrollmentStatus
          open={updateEnrollmentStatusDialogOpen}
          onOpenChange={(nextOpen) => {
            setUpdateEnrollmentStatusDialogOpen(nextOpen);
            if (!nextOpen) setSelectedEnrollmentForStatus(null);
          }}
          cohortId={selectedEnrollmentForStatus?.cohortId}
          userId={selectedEnrollmentForStatus?.userId}
          courseId={selectedEnrollmentForStatus?.courseId}
          currentStatus={selectedEnrollmentForStatus?.currentStatus}
          studentName={selectedEnrollmentForStatus?.studentName}
          cohortName={selectedEnrollmentForStatus?.cohortName}
          courseName={selectedEnrollmentForStatus?.courseName}
          refetchEnrolments={onEnrollmentUpdated}
          onUpdated={() => {
            setUpdateEnrollmentStatusDialogOpen(false);
            setSelectedEnrollmentForStatus(null);
            setSelectedIds(new Set());
          }}
        />
      )}

      {allowEnrolmentActions && (
        <DeleteStudentCohortEnrollment
          open={deleteEnrollmentDialogOpen}
          onOpenChange={(nextOpen) => {
            setDeleteEnrollmentDialogOpen(nextOpen);
            if (!nextOpen) setSelectedEnrollment(null);
          }}
          cohortId={selectedEnrollment?.cohortId}
          userId={selectedEnrollment?.userId}
          courseId={selectedEnrollment?.courseId}
          studentName={selectedEnrollment?.studentName}
          cohortName={selectedEnrollment?.cohortName}
          refetchEnrolments={onEnrollmentDeleted}
          onDeleted={() => {
            setDeleteEnrollmentDialogOpen(false);
            setSelectedEnrollment(null);
          }}
        />
      )}
    </div>
  )
}

export default StudentTable